'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Users, ArrowRight, Sparkles, Filter, ChevronDown } from 'lucide-react';
import { EntityType, EntitySearchData } from '@/types/tags';
import { ENTITY_CONFIG, ENTITY_TYPE_ORDER } from '@/lib/tags-config';
import { XIcon } from '@/components/icons/SocialIcons';

interface TrendingEntitiesSidebarProps {
    entities: EntitySearchData[];
    onEntitySelect: (entityId: string, entitySlug: string) => void;
    currentEntityId?: string;
    /** Where "Browse Full Directory" points. Chain pages pass `/<chain>/entities` so the
        user stays in the chain lens; defaults to the global `/tags` directory. */
    directoryHref?: string;
}

type SortOption = 'popular' | 'alphabetical' | 'accounts';
type FilterOption = 'all' | 'twitter' | EntityType;

export default function TrendingEntitiesSidebar({ entities, onEntitySelect, currentEntityId, directoryHref = '/tags' }: TrendingEntitiesSidebarProps) {
    const [sortBy, setSortBy] = useState<SortOption>('popular');
    const [filterBy, setFilterBy] = useState<FilterOption>('twitter');
    const [showAll, setShowAll] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Count entities by type for filter badges
    const entityCounts = useMemo(() => {
        const counts: Record<string, number> = { all: entities.length, twitter: 0 };
        
        entities.forEach(entity => {
            if (entity.twitterHandles?.length > 0) counts.twitter++;
            if (entity.entityType) {
                counts[entity.entityType] = (counts[entity.entityType] || 0) + 1;
            }
        });
        
        return counts;
    }, [entities]);

    // Filter and sort entities
    const displayedEntities = useMemo(() => {
        let filtered = [...entities];

        // Apply filter
        if (filterBy === 'twitter') {
            filtered = filtered.filter(e => e.twitterHandles?.length > 0);
        } else if (filterBy !== 'all') {
            filtered = filtered.filter(e => e.entityType === filterBy);
        }

        // Apply sort
        switch (sortBy) {
            case 'alphabetical':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'accounts':
                filtered.sort((a, b) => (b.twitterHandles?.length || 0) - (a.twitterHandles?.length || 0));
                break;
            case 'popular':
            default:
                // Sort by Twitter presence first, then by number of handles
                filtered.sort((a, b) => {
                    const aHasTwitter = a.twitterHandles?.length > 0 ? 1 : 0;
                    const bHasTwitter = b.twitterHandles?.length > 0 ? 1 : 0;
                    if (bHasTwitter !== aHasTwitter) return bHasTwitter - aHasTwitter;
                    return (b.twitterHandles?.length || 0) - (a.twitterHandles?.length || 0);
                });
                break;
        }

        return filtered;
    }, [entities, filterBy, sortBy]);

    const visibleEntities = showAll ? displayedEntities : displayedEntities.slice(0, 10);
    const hasMore = displayedEntities.length > 10;

    const sortLabels: Record<SortOption, string> = {
        popular: 'Most Active',
        alphabetical: 'A-Z',
        accounts: 'Most Accounts'
    };

    return (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden shadow-sm">
            {/* Header */}
            <div className="p-5 border-b border-gray-100 dark:border-neutral-800">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Explore Entities
                        </h2>
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`p-2 rounded-lg transition-colors ${showFilters ? 'bg-gray-100 dark:bg-neutral-800' : 'hover:bg-gray-100 dark:hover:bg-neutral-800'}`}
                    >
                        <Filter className="w-4 h-4 text-gray-500" />
                    </button>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {displayedEntities.length} {filterBy === 'twitter' ? 'with Twitter' : filterBy === 'all' ? 'total' : filterBy + 's'} • Click to view feed
                </p>

                {/* Expandable Filters */}
                {showFilters && (
                    <div className="mt-4 space-y-3 pt-3 border-t border-gray-100 dark:border-neutral-800">
                        {/* Sort Options */}
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                                Sort by
                            </label>
                            <div className="flex flex-wrap gap-1.5">
                                {(['popular', 'alphabetical', 'accounts'] as SortOption[]).map(option => (
                                    <button
                                        key={option}
                                        onClick={() => setSortBy(option)}
                                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                            sortBy === option
                                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                                : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                        }`}
                                    >
                                        {sortLabels[option]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type Filter */}
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                                Filter by type
                            </label>
                            <div className="flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => setFilterBy('twitter')}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                        filterBy === 'twitter'
                                            ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                                            : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                    }`}
                                >
                                    <XIcon className="w-3 h-3" />
                                    Twitter ({entityCounts.twitter})
                                </button>
                                <button
                                    onClick={() => setFilterBy('all')}
                                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                        filterBy === 'all'
                                            ? 'bg-gray-200 dark:bg-neutral-700 text-gray-800 dark:text-gray-200'
                                            : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                    }`}
                                >
                                    All ({entityCounts.all})
                                </button>
                                {ENTITY_TYPE_ORDER.filter(type => entityCounts[type] > 0).map(type => {
                                    const config = ENTITY_CONFIG[type];
                                    const Icon = config.icon;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setFilterBy(type)}
                                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                                filterBy === type
                                                    ? `${config.bgColor} ${config.color}`
                                                    : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                            }`}
                                        >
                                            <Icon className="w-3 h-3" />
                                            {entityCounts[type]}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Entity List — capped on small screens; on lg the bounded sticky column
                owns the scroll (avoids a confusing nested double-scroll). */}
            <div className="divide-y divide-gray-100 dark:divide-neutral-800 max-h-[500px] lg:max-h-none overflow-y-auto">
                {visibleEntities.length > 0 ? (
                    visibleEntities.map((entity, index) => {
                        const isActive = currentEntityId === entity.id;
                        const hasTwitter = entity.twitterHandles?.length > 0;
                        
                        return (
                            <button
                                key={entity.id}
                                onClick={() => onEntitySelect(entity.id, entity.slug)}
                                className={`w-full p-3 transition-all text-left group relative ${
                                    isActive
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                                        : 'hover:bg-gray-50 dark:hover:bg-neutral-800/50'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Entity Image */}
                                    <div className="flex-shrink-0 relative">
                                        {entity.primaryImage ? (
                                            <img
                                                src={entity.primaryImage}
                                                alt={entity.name}
                                                className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-neutral-700"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-neutral-800 dark:to-neutral-700 flex items-center justify-center text-sm font-bold text-gray-400">
                                                {entity.name.charAt(0)}
                                            </div>
                                        )}
                                        {/* Twitter indicator */}
                                        {hasTwitter && (
                                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-sky-500 rounded-full flex items-center justify-center border-2 border-white dark:border-neutral-900">
                                                <XIcon className="w-2 h-2 text-white" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Entity Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className={`font-semibold text-sm truncate transition-colors ${
                                                isActive
                                                    ? 'text-blue-600 dark:text-blue-400'
                                                    : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                                            }`}>
                                                {entity.name}
                                            </h3>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                                {entity.entityType}
                                            </span>
                                            {hasTwitter && (
                                                <>
                                                    <span className="text-gray-300 dark:text-gray-600">•</span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {entity.twitterHandles.length} {entity.twitterHandles.length === 1 ? 'account' : 'accounts'}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Arrow indicator */}
                                    <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all ${
                                        isActive
                                            ? 'text-blue-500 translate-x-0'
                                            : 'text-gray-400 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
                                    }`} />
                                </div>
                            </button>
                        );
                    })
                ) : (
                    <div className="p-6 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            No entities match this filter
                        </p>
                    </div>
                )}
            </div>

            {/* Show More / Less */}
            {hasMore && (
                <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full p-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors flex items-center justify-center gap-1 border-t border-gray-100 dark:border-neutral-800"
                >
                    {showAll ? 'Show Less' : `Show ${displayedEntities.length - 10} More`}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
                </button>
            )}

            {/* Link to Browse Directory */}
            <div className="p-4 border-t border-gray-100 dark:border-neutral-800">
                <Link
                    href={directoryHref}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-scout-gold/10 border border-scout-gold/30 hover:bg-scout-gold/20 transition-colors group w-full justify-center"
                >
                    <Users className="w-4 h-4 text-scout-gold" />
                    <span className="text-sm font-medium text-scout-gold group-hover:text-[#05b35f]">
                        Browse Full Directory →
                    </span>
                </Link>
            </div>
        </div>
    );
}
